import React from "react";
import { useTranslation } from "react-i18next";
import {
  $Button,
  $Input,
  $Select,
  $Row,
  $Col,
  $DatePicker,
  $Skeleton,
  $Switch,
  $Popover,
} from "us.common/components";
import moment from "moment";
import { ExclamationCircleFilled } from "us.icons";

type BasicInfoProps = {
  isFetching: boolean;
  isSaving: boolean;
  currentDateFormat: string;
  onchangeType: (value: any, rest: any) => void;
  handleBirthday: (value: any, rest: any) => void;
  handlePersonNo: (value: any, rest: any) => void;
  formatOrganizationNo: (value: any, rest: any) => void;
  handleAddressUnknown: (checked: boolean, rest: any, values: any) => void;
  addressUnknownConfirmVisible: boolean;
  postalCodePopOverConfirmYes: (rest: any) => void;
  postalCodePopOverConfirmNo: (rest: any) => void;
  values: any;
  rest: any;
};
/**
 * @description
 * BasicInfo Component
 * -------------------
 * This component renders the main debtor basic information form.
 *
 * Features:
 *  - Handles both Person (P) and Company (N) types
 *  - Dynamically switches fields based on debtor type
 *  - Manages personal details (name, gender, birthdate)
 *  - Handles company details (organization number)
 *  - Collects contact information (email, phone, etc.)
 *  - Controls "Address Unknown" toggle with confirmation popover
 */
const BasicInfo: React.FC<BasicInfoProps> = (props) => {
  const { t } = useTranslation();
  const {
    values,
    rest,
    isFetching,
    isSaving,
    currentDateFormat,
    handlePersonNo,
    handleBirthday,
    onchangeType,
    formatOrganizationNo,
    postalCodePopOverConfirmNo,
    postalCodePopOverConfirmYes,
    addressUnknownConfirmVisible,
    handleAddressUnknown,
  } = props;

  return (
    <div className="pl-1 pb-4">
      <$Skeleton loading={isFetching || isSaving}>
        <$Row gutter={16}>
          <$Col span={6}>
            <$Select
              formitem={{
                label: t("US.COLLECTION.DEBTOR:GENERAL.TYPE"),
              }}
              name="debtorType"
              allOption={false}
              size="small"
              onSelect={(e: any) => onchangeType(e, rest)}
              value={values.debtorType}
              options={[
                {
                  label: t("US.COLLECTION.DEBTOR:GENERAL.PERSON"),
                  value: "P",
                },
                {
                  label: t("US.COLLECTION.DEBTOR:GENERAL.COMPANY"),
                  value: "N",
                },
              ]}
              data-testid="debtor-general-debtorType"
            />
          </$Col>
          <$Col span={9}>
            <$Input
              name="firstName"
              label={t("US.COLLECTION.DEBTOR:GENERAL.FIRSTNAME")}
              defaultValue={values.firstName}
              size="small"
              data-testid="debtor-general-firstName"
            />
          </$Col>
          <$Col span={9}>
            <$Input
              name="lastName"
              label={t("US.COLLECTION.DEBTOR:GENERAL.LASTNAME")}
              defaultValue={values.lastName}
              required
              size="small"
              data-testid="debtor-general-lastName"
            />
          </$Col>
        </$Row>

        <$Row gutter={16} className="mt-2">
          <$Col span={6}>
            {values.debtorType === "P" && (
              <$Input
                label={t("US.COLLECTION.DEBTOR:GENERAL.PERSONNO")}
                name="personNo"
                onBlur={(value: any) => handleBirthday(value, rest)}
                defaultValue={values.personNo}
                size="small"
                data-testid="debtor-general-personNo"
              />
            )}
            {values.debtorType === "N" && (
              <$Input
                label={t("US.COLLECTION.DEBTOR:GENERAL.COMPANY_NO")}
                name="companyNo"
                defaultValue={values.companyNo}
                size="small"
                data-testid="debtor-general-companyNo"
                onBlur={(value: any) => formatOrganizationNo(value, rest)}
              />
            )}
          </$Col>
          {values.debtorType === "P" && (
            <$Col span={9}>
              <$Row gutter={16}>
                <$Col span={12}>
                  <$Select
                    formitem={{
                      label: t("US.COLLECTION.DEBTOR:GENERAL.GENDER"),
                    }}
                    name="gender"
                    size="small"
                    allOption={false}
                    defaultValue={values.gender}
                    options={[
                      {
                        label: t("US.COMMON:COMMON.MALE"),
                        value: "M",
                      },
                      {
                        label: t("US.COMMON:COMMON.FEMALE"),
                        value: "F",
                      },
                    ]}
                    data-testid="debtor-general-gender"
                  />
                </$Col>
                <$Col span={12}>
                  <$DatePicker
                    name="birthDate"
                    label={t("US.COLLECTION.DEBTOR:GENERAL.BIRTHDATE")}
                    placeholder={currentDateFormat}
                    format={currentDateFormat}
                    onChange={(e: any) => handlePersonNo(e, rest)}
                    defaultPickerValue={moment("2000/01/01")}
                    value={
                      moment(values.birthDate).isValid()
                        ? moment(values.birthDate)
                        : ""
                    }
                    disabledDate={(current: any) => {
                      return !(current && current < moment().endOf("day"));
                    }}
                    style={{
                      width: "100%",
                    }}
                    size="small"
                    data-testid="debtor-general-birthDate"
                    allowClear
                  />
                </$Col>
              </$Row>
            </$Col>
          )}
          <$Col span={9}>
            <$Input
              name="email"
              label={t("US.COLLECTION.DEBTOR:GENERAL.EMAIL")}
              defaultValue={values.email?.trim()}
              size="small"
              data-testid="debtor-general-email"
            />
          </$Col>
        </$Row>

        <$Row gutter={16} className="mt-2">
          <$Col span={6}>
            <$Input
              name="sms"
              label={t("US.COLLECTION.DEBTOR:GENERAL.SMS")}
              defaultValue={values.sms}
              size="small"
              data-testid="debtor-general-sms"
            />
          </$Col>
          <$Col span={6}>
            <$Input
              name="mobile"
              label={t("US.COLLECTION.DEBTOR:GENERAL.MOBILE")}
              defaultValue={values.mobile}
              size="small"
            />
          </$Col>
          <$Col span={6}>
            <$Input
              name="home"
              label={t("US.COLLECTION.DEBTOR:GENERAL.TELEPHONE")}
              defaultValue={values.home}
              size="small"
              data-testid="debtor-general-telephone"
            />
          </$Col>
          <$Col span={6}>
            <$Input
              name="work"
              label={t("US.COLLECTION.DEBTOR:GENERAL.WORK")}
              defaultValue={values.work}
              size="small"
            />
          </$Col>
        </$Row>
        <$Row gutter={16}>
          <$Col span={24}>
            <$Popover
              placement="topLeft"
              content={
                <div
                  style={{
                    maxWidth: "300px",
                  }}
                >
                  <div className="ant-popover-message">
                    <ExclamationCircleFilled />
                    <div className="ant-popover-message-title">
                      <div
                        style={{
                          paddingBottom: "0.3rem",
                        }}
                      >
                        {t(
                          "US.COLLECTION.DEBTOR:GENERAL.ADDING_ADDRESS_WILL_CHANGE_AS_KNOWN_ADDRESS",
                        )}
                      </div>
                      <div
                        style={{
                          paddingBottom: "0.5rem",
                        }}
                      >
                        <strong>
                          {t(
                            "US.COLLECTION.DEBTOR:GENERAL.DO_YOU_WANT_TO_CONTINUE",
                          )}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="ant-popover-buttons">
                    <$Button
                      size="small"
                      onClick={() => postalCodePopOverConfirmNo(rest)}
                      data-testid="debtor-general-addressChangeConfirmNo"
                    >
                      {t("US.COMMON:COMMON.NO")}
                    </$Button>
                    <$Button
                      size="small"
                      type="primary"
                      onClick={() => postalCodePopOverConfirmYes(rest)}
                      data-testid="debtor-general-addressChangeConfirmYes"
                    >
                      {t("US.COMMON:COMMON.YES")}
                    </$Button>
                  </div>
                </div>
              }
              trigger="click"
              visible={addressUnknownConfirmVisible}
            >
              <div className="mt-3 d-flex flex-1 align-items-top">
                <div className="mr-3">
                  <$Switch
                    name="addrUnknown"
                    value={values.addrUnknown}
                    checked={values.addrUnknown}
                    onChange={(checked: any) => {
                      rest.setFieldValue("addrUnknown", checked);
                      handleAddressUnknown(checked, rest, values);
                    }}
                    data-testid="debtor-general-addrUnknown"
                  />
                </div>
                <div className="d-flex flex-column ml-2">
                  <span>
                    {t("US.COLLECTION.DEBTOR:GENERAL.ADDRESSUNKNOWN")}
                  </span>
                  <small>
                    (
                    {t(
                      "US.COLLECTION.DEBTOR:GENERAL.NO_LETTER_WILL_SEND_TO_THE_PARTY_WHEN_THE_ADDRESS_IS_UNKNOWN",
                    )}
                    )
                  </small>
                </div>
              </div>
            </$Popover>
          </$Col>
        </$Row>
      </$Skeleton>
    </div>
  );
};

export default BasicInfo;
